import React, { useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TabellaListaServer } from "../../../../components/Tabella/TabellaListaServer";
import { ChipFilter } from "../../../../components/Tabella/ChipFilter";
import { RangeFilterButton } from "../../../../components/Tabella/RangeFilterButton";
import MyApiClient from "../../../../../api-client/MyApiClient";
import { Modal, Spinner, Button, Row, Col } from "react-bootstrap";
import { OMAGGIO } from "../../../../components/Vendite/formePagamento";
import { formattaPrezzo } from "../../../../components/Util/prezzi";

// Valori dei filtri a chip: quelli che il server riconosce, con le etichette della lista.
const STATO_OPTIONS = [
    { value: 'UNPAID', label: 'Non pagato' },
    { value: 'PAID', label: 'Pagato' },
    { value: 'CANCELLED', label: 'Annullato' },
    { value: 'REFUNDED', label: 'Rimborsato' },
];

const TIPOLOGIA_OPTIONS = [
    { value: 'subscription', label: 'Abbonamento' },
    { value: 'course', label: 'Corso' },
    { value: 'lesson', label: 'Lezione' },
    { value: 'venue', label: 'Impianto' },
];

// Intervalli nella finestra del pulsante "Date e prezzo".
const INTERVALLI = [
    { key: 'dataOrdine', label: 'Data ordine', type: 'date' },
    { key: 'dataPrenotazione', label: 'Data prenotazione', type: 'date' },
    { key: 'prezzo', label: 'Prezzo', type: 'number' },
    { key: 'dataPagamento', label: 'Data pagamento', type: 'date' },
];

/**
 * Traduce i filtri della tabella nei parametri dell'API. I nomi delle colonne
 * sono quelli di react-table, quelli dei parametri sono del server: la
 * corrispondenza sta qui, in un posto solo.
 *
 * Lo stato resta fuori perche' viaggia gia' per conto suo.
 */
export const parametriDaiFiltri = (filters = []) => {
    const valore = id => filters.find(f => f.id === id)?.value;
    const intervallo = (id, nomeDa, nomeA) => {
        const v = valore(id) || {};
        return { ...(v.da && { [nomeDa]: v.da }), ...(v.a && { [nomeA]: v.a }) };
    };

    return {
        ...(valore('bookingType') && { bookingType: valore('bookingType') }),
        ...(valore('playerName') && { cliente: valore('playerName') }),
        ...intervallo('price', 'prezzoDa', 'prezzoA'),
        ...intervallo('createdAt', 'creatoDa', 'creatoA'),
        ...intervallo('bookingDate', 'prenotatoDa', 'prenotatoA'),
        ...intervallo('paymentDate', 'pagatoDa', 'pagatoA'),
    };
};

export const SalesTable = () => {
    const navigate = useNavigate();


    const [data_json, setDataJson] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(null);
    // Chip a scelta multipla: all'inizio tutti accesi (= nessun filtro)
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS.map((o) => o.value));
    const [tipologiaFilter, setTipologiaFilter] = useState(TIPOLOGIA_OPTIONS.map((o) => o.value));
    const [dataOrdineFilter, setDataOrdineFilter] = useState(undefined);
    const [dataPrenotazioneFilter, setDataPrenotazioneFilter] = useState(undefined);
    const [prezzoFilter, setPrezzoFilter] = useState(undefined);
    const [dataPagamentoFilter, setDataPagamentoFilter] = useState(undefined);
    const fetchIdRef = useRef(0);
    const lastFetchArgsRef = useRef(null);

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const fetchData = useCallback(async (args) => {
        const { pageIndex, pageSize, globalFilter, sortBy, externalFilters } = args;
        // react-table tiene un elenco di ordinamenti, il server ne accetta uno solo.
        const ordinamento = (sortBy && sortBy[0]) || null;
        lastFetchArgsRef.current = args;
        const fetchId = ++fetchIdRef.current;

        // Stati e tipologie scelti: tutti = nessun filtro, alcuni = elenco separato da virgole,
        // nessuno = nessun ordine (senza chiamare il server)
        const perApi = (scelti, opzioni) => {
            const lista = Array.isArray(scelti) ? scelti : (scelti ? [scelti] : opzioni.map((o) => o.value));
            return lista.length === opzioni.length ? null : lista.join(',');
        };
        const statiScelti = perApi(externalFilters?.stato, STATO_OPTIONS);
        const tipologieScelte = perApi(externalFilters?.tipologia, TIPOLOGIA_OPTIONS);
        if (statiScelti === '' || tipologieScelte === '') {
            setDataJson([]);
            setTotalItems(0);
            setPageCount(1);
            return;
        }

        setLoadingShowed(true);

        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                let token = tokenDetails.idToken;
                const apiClient = new MyApiClient();

                const offset = pageIndex * pageSize;
                const limit = pageSize;
                
                // Lo stato viaggia per conto suo; gli altri filtri (chip, intervalli e
                // la ricerca, che cerca per cliente) passano da parametriDaiFiltri.
                const status = statiScelti;
                const filters = [
                    { id: 'bookingType', value: tipologieScelte },
                    { id: 'playerName', value: globalFilter },
                    { id: 'price', value: externalFilters?.prezzo },
                    { id: 'createdAt', value: externalFilters?.dataOrdine },
                    { id: 'bookingDate', value: externalFilters?.dataPrenotazione },
                    { id: 'paymentDate', value: externalFilters?.dataPagamento },
                ];

                let bookingData = await apiClient.getUserBookings(
                    token,
                    limit,
                    offset,
                    status,
                    ordinamento?.id ?? null,
                    ordinamento?.desc === false ? 'asc' : 'desc',
                    parametriDaiFiltri(filters),
                );

                if (fetchId === fetchIdRef.current) {
                    setLoadingShowed(false);
                    // Richiesta rifiutata dal server: lo si dice, invece di mostrare una lista vuota
                    if (bookingData && bookingData.error) {
                        setDataJson([]);
                        setTotalItems(null);
                        setModalTitle('Impossibile caricare gli ordini');
                        setModalText(`Il server ha rifiutato la richiesta: ${bookingData.error}`);
                        setModalShowed(true);
                        return;
                    }
                    if(bookingData) {
                        let data = [];
                        if (bookingData.items) {
                            bookingData.items.forEach(element => {
                                
                                let statusObj = null;
                                if(element.status === 'PAID') {
                                    // L'omaggio è chiuso come un pagato, ma non è un incasso:
                                    // nell'elenco si legge per quello che è.
                                    statusObj = element.payment_method === OMAGGIO
                                        ? {
                                            "label": "Omaggio",
                                            "class": "text-info"
                                        }
                                        : {
                                            "label": "Pagato",
                                            "class": "text-success"
                                        };
                                } else if(element.status === 'UNPAID') {
                                    statusObj = {
                                        "label": "Non pagato",
                                        "class": "text-danger"
                                    };
                                } else if(element.status === 'REFUNDED') {
                                    statusObj = {
                                        "label": "Rimborsato",
                                        "class": "text-warning"
                                    };
                                } else if(element.status === 'CANCELLED') {
                                    statusObj = {
                                        "label": "Annullato",
                                        "class": "text-error"
                                    };
                                }

                                const options = { year: 'numeric', month: '2-digit', day: '2-digit' };

                                // Data e orario separati: in tabella l'orario va in piccolo sotto la data
                                let bookingDateStr = '-';
                                let bookingTimeStr = '';
                                if (element.bookingDate) {
                                    bookingDateStr = new Date(element.bookingDate).toLocaleDateString('it-IT', options);
                                    if (element.startTime && element.endTime) {
                                        bookingTimeStr = `${element.startTime}-${element.endTime}`;
                                    }
                                }
                                
                                let paymentDateStr = '-';
                                if (element.paymentDate) {
                                    paymentDateStr = new Date(element.paymentDate).toLocaleDateString('it-IT', options);
                                }

                                let createdAtStr = '-';
                                let createdAtTimeStr = '';
                                if (element.createdAt) {
                                    const creato = new Date(element.createdAt);
                                    createdAtStr = creato.toLocaleDateString('it-IT', options);
                                    createdAtTimeStr = creato.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                                }

                                let detail = '';
                                if (element.venue_name) detail = element.venue_name;
                                else if (element.course_name) detail = element.course_name;
                                else if (element.lesson_name) detail = element.lesson_name;
                                else if (element.subscription_name) detail = element.subscription_name;

                                let typeDecoded = element.bookingType;
                                if(element.bookingType === 'venue') typeDecoded = 'Impianto';
                                else if(element.bookingType === 'subscription') typeDecoded = 'Abbonamento';
                                else if(element.bookingType === 'course') typeDecoded = 'Corso';
                                else if(element.bookingType === 'lesson') typeDecoded = 'Lezione';

                                let row = {
                                    "id": element.id,
                                    "bookingType": typeDecoded,
                                    "originalBookingType": element.bookingType,
                                    "orderID": element.orderID,
                                    "detail": detail,
                                    "playerName": element.juniorName ? `${element.playerName} (${element.juniorName})` : element.playerName,
                                    "userId": element.userId || element.playerId || element.playerID || element.user_id || element.player_id,
                                    "createdAt": createdAtStr,
                                    "createdAtTime": createdAtTimeStr,
                                    "bookingTime": bookingTimeStr,
                                    "price": formattaPrezzo(element.price),
                                    "status": statusObj,
                                    "paymentDate": paymentDateStr,
                                    "bookingDate": bookingDateStr,
                                    "invoiceNumber": element.invoice_number_str || '-',
                                    "originalStatus": element.status
                                };
                                data.push(row);
                            });
                        }
                        
                        setDataJson(data);
                        
                        // Calculate pageCount
                        const totalItems = bookingData.numberOfItems || 0;
                        setTotalItems(totalItems > 0 || data.length === 0 ? totalItems : null);
                        if (totalItems > 0) {
                            setPageCount(Math.ceil(totalItems / pageSize));
                        } else {
                            if (bookingData.items && bookingData.items.length === pageSize) {
                                setPageCount(pageIndex + 2);
                            } else {
                                setPageCount(pageIndex + 1);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            if (fetchId === fetchIdRef.current) {
                setLoadingShowed(false);
                console.error("Error fetching data:", error);
            }
        }
    }, []);

    function openDetail(row) {
        if(row.originalBookingType === 'subscription') {
            navigate('/struttura/booking/userSubscriptions/'+row.id);
        } else if(row.originalBookingType === 'course') {
            navigate('/struttura/booking/userCourses/'+row.id);
        } else if(row.originalBookingType === 'venue') {
            navigate('/struttura/booking/uservenues/'+row.id);
        } else if(row.originalBookingType === 'lesson') {
            navigate('/struttura/booking/userLessons/'+row.id);
        }
    }

    const data_column = [
        {
            Header: 'ID Ordine',
            accessor: 'orderID',
            disableFilters: true
        },
        {
            Header: 'Data Ordine',
            accessor: 'createdAt',
        },
        {
            Header: 'Tipologia',
            accessor: 'bookingType',
        },
        {
            Header: 'Cliente',
            accessor: 'playerName',
            disableSortBy: true,
            Cell: ({ row }) => {
                const playerName = row.original.playerName;
                const userId = row.original.userId;
                if (userId) {
                    return (
                        <Link to={`/struttura/clienti/player_profile/${userId}`} className="sid-plain-link">
                            {playerName}
                        </Link>
                    );
                }
                return <span>{playerName}</span>;
            }
        },
        {
            Header: 'Prezzo',
            accessor: 'price',
        },
        {
            Header: 'Stato',
            accessor: 'status',
            Cell: ({ row }) => {
                const status = row.original.status;
                return (
                    <div className="d-flex align-items-center">
                        <i className={`fa fa-circle me-1 ${status ? status.class : ''}`}></i>
                        {status ? status.label : ''}
                    </div>
                )
            },
        },
    ];

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>

            <TabellaListaServer
                pageList={true}
                openColumn={'orderID'}
                openRow={openDetail}
                // Gli ordini di esempio (dati finti in sviluppo) non hanno una scheda da aprire
                canOpenRow={(row) => !String(row.id).startsWith('esempio-')}
                cardTitle={'Ordini'}
                sidLayout={true}
                hasSearch={true}
                searchPlaceholder={'Cerca per cliente'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                        <ChipFilter label="Tipologia" options={TIPOLOGIA_OPTIONS} value={tipologiaFilter} onChange={setTipologiaFilter} multiple />
                        <RangeFilterButton
                            label="Date e prezzo"
                            ranges={INTERVALLI}
                            value={{
                                dataOrdine: dataOrdineFilter,
                                dataPrenotazione: dataPrenotazioneFilter,
                                prezzo: prezzoFilter,
                                dataPagamento: dataPagamentoFilter,
                            }}
                            onApply={(nuovi) => {
                                setDataOrdineFilter(nuovi.dataOrdine);
                                setDataPrenotazioneFilter(nuovi.dataPrenotazione);
                                setPrezzoFilter(nuovi.prezzo);
                                setDataPagamentoFilter(nuovi.dataPagamento);
                            }}
                        />
                    </div>
                }
                externalFilters={{
                    stato: statoFilter,
                    tipologia: tipologiaFilter,
                    dataOrdine: dataOrdineFilter,
                    dataPrenotazione: dataPrenotazioneFilter,
                    prezzo: prezzoFilter,
                    dataPagamento: dataPagamentoFilter,
                }}
                hasAddMore={false}
                data_column={data_column}
                data_json={data_json}
                manualPagination={true}
                pageCount={pageCount}
                totalItems={totalItems}
                fetchData={fetchData}
                sort_by={'createdAt'}
                sort_type={'desc'}
                manualGlobalFilter={true}
                manualFilters={true}
            />
        </>
    )
}

export default SalesTable;
